/**
 * Impostazioni della squadra-stagione (V43), solo per l'Admin.
 *
 * Due regole diverse, e la vista le dice entrambe: il formato e copiato sulla
 * partita alla creazione, quindi non tocca quelle gia create; i tipi di evento
 * si leggono a ogni caricamento del timer, quindi valgono subito anche per
 * quelle, e gli eventi gia registrati di un tipo spento restano. Gol, giallo e
 * rosso non si spengono.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Formato, TipoEvento, TipoEventoConfigurabile } from '@calcio/shared';
import { OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  impostazioniSquadraSchema,
  puo,
  TIPI_EVENTO_CONFIGURABILI,
  TIPI_EVENTO_FISSI,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { CampiFormato } from '../partite/CampiFormato.js';
import { ETICHETTA_EVENTO } from '../partite/partita.js';

interface LetturaImpostazioni {
  squadra: string;
  stagione: string;
  categoria: string | null;
  campionato: string | null;
  girone: string | null;
  formato: Formato;
  tipiEventoAttivi: TipoEvento[];
  partiteProgrammate: number;
}

export function ImpostazioniSquadra() {
  const { ss } = useParams<{ ss: string }>();
  const { utente } = useContesto();
  const gestisce = Boolean(ss && puo(utente.assegnazioni, 'societa.squadre.gestisci', ss));
  const { dati, errore, ricarica } = useDati<LetturaImpostazioni>(
    gestisce ? `/squadre-stagione/${ss}/impostazioni` : '',
  );

  if (!gestisce) return <NonTrovata />;
  if (!dati) {
    return (
      <div>
        <Testata titolo="Impostazioni squadra" voce="impostazioni" />
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </div>
    );
  }
  // Cambiando squadra dal selettore il form riparte dai valori dell'altra.
  return <Form key={ss} ss={ss!} lettura={dati} ricarica={ricarica} />;
}

function Form({
  ss,
  lettura,
  ricarica,
}: {
  ss: string;
  lettura: LetturaImpostazioni;
  ricarica: () => Promise<void>;
}) {
  const { ricaricaSquadre } = useContesto();
  const [categoria, setCategoria] = useState(lettura.categoria ?? '');
  const [campionato, setCampionato] = useState(lettura.campionato ?? '');
  const [girone, setGirone] = useState(lettura.girone ?? '');
  const [formato, setFormato] = useState<Formato>(lettura.formato);
  const [tipi, setTipi] = useState(() =>
    TIPI_EVENTO_CONFIGURABILI.filter((t) => lettura.tipiEventoAttivi.includes(t)),
  );
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  // L'esito dell'ultimo salvataggio non descrive piu un form cambiato.
  useEffect(() => {
    setErroreServer(null);
  }, [categoria, campionato, girone, formato, tipi]);

  const cambiaTipo = (tipo: TipoEventoConfigurabile, attivo: boolean) =>
    setTipi((t) => (attivo ? [...t, tipo] : t.filter((x) => x !== tipo)));

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = impostazioniSquadraSchema.safeParse({
      categoria,
      campionato,
      girone,
      formato,
      // Un tipo fisso spento nel database (dati precedenti) si riaccende qui.
      tipiEventoAttivi: [...new Set([...TIPI_EVENTO_FISSI, ...tipi])],
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/squadre-stagione/${ss}/impostazioni`, { metodo: 'PUT', corpo: esito.data });
      // La categoria compare nel selettore e nella scelta del contesto.
      await Promise.all([ricarica(), ricaricaSquadre()]);
      toast.success('Impostazioni salvate.');
    } catch (errore) {
      setErroreServer(messaggio(errore));
    } finally {
      setInCorso(false);
    }
  };

  const programmate = lettura.partiteProgrammate;

  return (
    <form onSubmit={(e) => void invia(e)}>
      <Testata
        titolo="Impostazioni squadra"
        voce="impostazioni"
        sopra={`${lettura.squadra} · ${lettura.stagione}`}
      />
      <Corpo className="flex max-w-2xl flex-col gap-8">
        <FieldGroup>
          <div className="grid gap-4 @2xl:grid-cols-3">
            <CampoTesto
              id="impostazioni-categoria"
              etichetta="Categoria"
              valore={categoria}
              onCambia={setCategoria}
              errore={errori.categoria}
              segnaposto="Allievi"
            />
            <CampoTesto
              id="impostazioni-campionato"
              etichetta="Campionato"
              valore={campionato}
              onCambia={setCampionato}
              errore={errori.campionato}
              segnaposto="Regionale"
            />
            <CampoTesto
              id="impostazioni-girone"
              etichetta="Girone"
              valore={girone}
              onCambia={setGirone}
              errore={errori.girone}
              segnaposto="B"
            />
          </div>
        </FieldGroup>

        <FieldSet>
          <FieldLegend>Formato di gioco</FieldLegend>
          <FieldDescription>
            È copiato su ogni partita quando la si crea: modificarlo non tocca le partite già
            create.
            {programmate === 1 &&
              ' Quella ancora programmata tiene il suo formato, che si cambia dalla sua pagina.'}
            {programmate > 1 &&
              ` Le ${programmate} ancora programmate tengono il loro formato, che si cambia dalla pagina di ciascuna.`}
          </FieldDescription>
          <CampiFormato formato={formato} onCambia={setFormato} errore={errori.formato} />
        </FieldSet>

        <FieldSet>
          <FieldLegend>Tipi di evento attivi</FieldLegend>
          <FieldDescription>
            I pulsanti sotto il nome del giocatore nel Live Timer. Sulle categorie giovanili
            conviene tenerne pochi, per limitare gli errori di battitura in panchina. Valgono
            subito, anche nelle partite già create; gli eventi già registrati restano.
          </FieldDescription>
          <FieldGroup className="grid gap-3 @lg:grid-cols-2">
            {TIPI_EVENTO_CONFIGURABILI.map((tipo) => {
              const fisso = TIPI_EVENTO_FISSI.includes(tipo);
              const id = `tipo-${tipo}`;
              return (
                <Field key={tipo} orientation="horizontal">
                  <Checkbox
                    id={id}
                    checked={fisso || tipi.includes(tipo)}
                    disabled={fisso}
                    onCheckedChange={(c) => cambiaTipo(tipo, c === true)}
                  />
                  <FieldLabel htmlFor={id} className="font-normal">
                    {ETICHETTA_EVENTO[tipo]}
                    {fisso && <span className="text-muted-foreground"> · sempre attivo</span>}
                  </FieldLabel>
                </Field>
              );
            })}
          </FieldGroup>
          {errori.tipiEventoAttivi && <FieldError>{errori.tipiEventoAttivi}</FieldError>}
        </FieldSet>

        {erroreServer && (
          <Alert variant="destructive">
            <OctagonXIcon />
            <AlertTitle>Salvataggio non riuscito</AlertTitle>
            <AlertDescription>{erroreServer}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <PulsanteInvio type="submit" inCorso={inCorso}>
            Salva
          </PulsanteInvio>
        </div>
      </Corpo>
    </form>
  );
}

function CampoTesto({
  id,
  etichetta,
  valore,
  onCambia,
  errore,
  segnaposto,
}: {
  id: string;
  etichetta: string;
  valore: string;
  onCambia: (valore: string) => void;
  errore?: string;
  segnaposto: string;
}) {
  return (
    <Field data-invalid={Boolean(errore)}>
      <FieldLabel htmlFor={id}>{etichetta}</FieldLabel>
      <Input
        id={id}
        value={valore}
        placeholder={segnaposto}
        aria-invalid={Boolean(errore)}
        onChange={(e) => onCambia(e.target.value)}
      />
      <FieldError>{errore}</FieldError>
    </Field>
  );
}
